import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, ArrowRight, Check, FileText, Download, Loader2, Sparkles, Lock, FolderDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth, API } from '../App';
import { STAGE2_FORMS, STAGE2_CELEBRATIONS } from '../config/stage2Forms';
import { formatErrorDetail } from '../utils/formatError';

// ============================================================================
// BRIAN'S CELEBRATION MESSAGES
// ============================================================================

const BRIAN_CELEBRATIONS = {
  stage00: {
    headline: "Assessment Complete",
    message: "You've taken the first step. Most people never get this far. Now I know who you are, what you bring, and how you think. Let's find your idea.",
    next: "Next up: Find Your Idea",
  },
  stage0: {
    headline: "You've Got an Idea",
    message: "Three ideas explored. One chosen. You didn't just pick something at random — you found the one that sits at the intersection of what you know and what the market needs. That's how it's done.",
    next: "Next up: Market Validation",
  },
  stage1: {
    headline: "Idea Validated",
    message: "You talked to real people. You listened. You now know whether the market wants what you're building. That puts you ahead of most entrepreneurs who skip this step entirely.",
    next: "Next up: Business Setup",
  },
  stage2: {
    headline: "Business Is Real",
    message: "It has a name. A structure. A bank account. You're not just thinking about being a business owner anymore — you are one. Now let's build the thing people will actually pay for.",
    next: "Next up: Tech & MVP",
  },
  stage3: {
    headline: "MVP Defined",
    message: "You know what to build, how to build it, and when it needs to be done. No more overthinking. You've got a plan. Now execute it.",
    next: "Next up: Capital & Fundraising",
  },
  stage4: {
    headline: "Finances Sorted",
    message: "You know your numbers. Startup costs, burn rate, break-even point. Most businesses fail because they run out of cash — you won't be one of them.",
    next: "Next up: Mentorship & Advisory",
  },
  stage5: {
    headline: "Network Activated",
    message: "You've mapped your support system. The people around you will make or break your business. Now go build those relationships.",
    next: "Next up: Growth & Brand",
  },
  stage6: {
    headline: "Brand Launched",
    message: "You have a brand. A voice. A plan to get customers. This is where it gets real — and exciting. Go make some noise.",
    next: "Next up: Scaling Operations",
  },
  stage7: {
    headline: "Programme Complete!",
    message: "You did it. From displaced worker to business owner. From uncertainty to a plan, a product, customers, and a future you built yourself. I'm genuinely proud of you.",
    next: "Welcome to the SPRING Alumni Community",
  },
};

// ============================================================================
// STAGE FORM CONFIGURATIONS
// ============================================================================

const STAGE_FORMS = {
  stage00: {
    title: "Entrepreneurial Assessment",
    subtitle: "Let's understand where you are and where you want to go.",
    sections: [
      {
        id: "about",
        title: "About You",
        description: "The basics — who you are and what you bring.",
        fields: [
          { id: "client_first_name", label: "What should we call you?", type: "text", placeholder: "Your first name", required: true },
          { id: "client_profession", label: "What's your profession or area of expertise?", type: "text", placeholder: "e.g., Software Engineer, Marketing Manager, Chef" },
          { id: "client_years_experience", label: "How many years of experience do you have?", type: "text", placeholder: "e.g., 15 years in retail management" },
        ]
      },
      {
        id: "situation",
        title: "Your Situation",
        description: "Where are you at right now?",
        fields: [
          { id: "client_situation", label: "Tell us about your current situation", type: "textarea", placeholder: "What's happening for you right now? What led you to SPRING?", rows: 4 },
          { id: "financial_urgency", label: "How urgent is your financial situation?", type: "radio", options: ["Low — I have savings or partner income", "Medium — Need income within 6-9 months", "High — Need income quickly"] },
        ]
      },
      {
        id: "preferences",
        title: "Your Preferences",
        description: "Help us tailor the programme to you.",
        fields: [
          { id: "restraint_of_trade", label: "Do you have a restraint of trade clause?", type: "radio", options: ["Not active", "Yes — 3 months remaining", "Yes — 6 months remaining", "Yes — 12 months remaining"] },
          { id: "learning_style", label: "How do you learn best?", type: "radio", options: ["Visual — I need to see it, diagrams, charts", "Reading/Writing — Give me the document", "Auditory — Talk me through it", "Kinesthetic — Let me try it hands-on"] },
        ]
      },
      {
        id: "mindset1",
        title: "Strategic Mindset",
        description: "There are no right or wrong answers here.",
        fields: [
          { id: "sm_response_1", label: "You've spotted an opportunity in your industry. What's the first thing you'd do?", type: "textarea", placeholder: "Describe your approach...", rows: 3 },
          { id: "sm_response_2", label: "A competitor launches something similar to your idea. What do you do?", type: "textarea", placeholder: "How would you respond?", rows: 3 },
        ]
      },
      {
        id: "mindset2",
        title: "Risk Tolerance",
        description: "How you handle uncertainty says a lot.",
        fields: [
          { id: "rt_response_1", label: "You need to invest $10,000 of your own money into your business. How does that make you feel?", type: "textarea", placeholder: "Be honest...", rows: 3 },
          { id: "rt_response_2", label: "Your first product launch gets mixed reviews. What's your next move?", type: "textarea", placeholder: "What would you do?", rows: 3 },
        ]
      },
      {
        id: "mindset3",
        title: "Self-Awareness",
        description: "Almost done. Two more questions.",
        fields: [
          { id: "si_response_1", label: "What's the biggest thing you'd need to learn to run a successful business?", type: "textarea", placeholder: "Where are your gaps?", rows: 3 },
          { id: "si_response_2", label: "Describe a time you had to adapt to a major change. What did you learn?", type: "textarea", placeholder: "Tell your story...", rows: 3 },
        ]
      },
      {
        id: "final",
        title: "One Last Thing",
        description: "Sum it up for us.",
        fields: [
          { id: "key_quote_1", label: "In one sentence — what does success look like for you?", type: "text", placeholder: "e.g., Financial freedom while doing work I love" },
        ]
      },
    ]
  },

  stage0: {
    title: "Find Your Idea",
    subtitle: "Discover a business idea that matches your skills, experience, and market opportunity.",
    sections: [
      {
        id: "background",
        title: "Your Background",
        description: "What do you bring to the table?",
        fields: [
          { id: "profession", label: "What's your profession or area of expertise?", type: "text", placeholder: "e.g., Marketing Manager, Software Developer, Chef", required: true },
          { id: "years_experience", label: "Years of experience in your field", type: "text", placeholder: "e.g., 12 years" },
          { id: "key_skills", label: "What are your strongest professional skills?", type: "textarea", placeholder: "List the skills you're most confident in...", rows: 3 },
        ]
      },
      {
        id: "problems",
        title: "Problems You See",
        description: "The best businesses solve real problems. What have you noticed?",
        fields: [
          { id: "problem_1", label: "Problem 1 — What's a frustration you've seen in your industry?", type: "textarea", placeholder: "Describe a real problem people face...", rows: 3 },
          { id: "problem_2", label: "Problem 2 — What else bugs you or the people around you?", type: "textarea", placeholder: "Another problem you've noticed...", rows: 3 },
          { id: "problem_3", label: "Problem 3 — Any other problems worth solving?", type: "textarea", placeholder: "One more...", rows: 3 },
        ]
      },
      {
        id: "ideas",
        title: "Your Ideas",
        description: "Based on your skills and the problems you see, brainstorm three ideas.",
        fields: [
          { id: "idea_1_name", label: "Idea 1 — What is it?", type: "text", placeholder: "Give it a name or short description" },
          { id: "idea_1_description", label: "How would it work?", type: "textarea", placeholder: "Briefly describe how this business would operate...", rows: 3 },
          { id: "idea_2_name", label: "Idea 2 — What is it?", type: "text", placeholder: "Give it a name or short description" },
          { id: "idea_2_description", label: "How would it work?", type: "textarea", placeholder: "Briefly describe how this business would operate...", rows: 3 },
          { id: "idea_3_name", label: "Idea 3 — What is it?", type: "text", placeholder: "Give it a name or short description" },
          { id: "idea_3_description", label: "How would it work?", type: "textarea", placeholder: "Briefly describe how this business would operate...", rows: 3 },
        ]
      },
      {
        id: "selection",
        title: "Choose Your Direction",
        description: "Which idea are you most drawn to? Trust your gut.",
        fields: [
          { id: "selected_idea", label: "Which idea are you going with?", type: "radio", options: ["Idea 1", "Idea 2", "Idea 3"], required: true },
          { id: "why_this_one", label: "Why this one?", type: "textarea", placeholder: "What makes this the right choice for you?", rows: 3 },
        ]
      },
    ]
  },

  stage1: {
    title: "Market Validation",
    subtitle: "Time to prove your idea works. Let's check the evidence.",
    sections: [
      {
        id: "idea",
        title: "Your Idea",
        description: "What are you building and for whom?",
        fields: [
          { id: "business_idea", label: "What's the business idea you're pursuing?", type: "textarea", placeholder: "Describe your business idea clearly...", rows: 3, required: true },
          { id: "target_customer", label: "Who is your target customer? Be specific.", type: "textarea", placeholder: "e.g., Busy working parents in Auckland aged 30-45 who...", rows: 3 },
          { id: "problem_statement", label: "What specific problem does it solve? In their words, not yours.", type: "textarea", placeholder: "The problem your customers experience...", rows: 3 },
        ]
      },
      {
        id: "discovery",
        title: "Customer Discovery",
        description: "Have you talked to real potential customers?",
        fields: [
          { id: "interviews_completed", label: "How many potential customers have you spoken to?", type: "radio", options: ["None yet — I need to start", "1-2 conversations", "3-5 conversations", "More than 5"] },
          { id: "customer_feedback", label: "What did they say? What was the most common feedback?", type: "textarea", placeholder: "Summarise what you heard...", rows: 4 },
        ]
      },
      {
        id: "market",
        title: "Market Signal",
        description: "The important questions about money.",
        fields: [
          { id: "willingness_to_pay", label: "Are they willing to pay?", type: "radio", options: ["Yes — multiple people said they'd pay", "Maybe — interest but no commitment", "No — they liked the idea but wouldn't pay", "Haven't asked yet"] },
          { id: "pricing_model", label: "What price range feels right? What did customers react to?", type: "text", placeholder: "e.g., $29/month subscription, $150 per session" },
          { id: "objections", label: "What objections or concerns did you hear?", type: "textarea", placeholder: "Every good idea gets pushback...", rows: 3 },
        ]
      },
      {
        id: "decision",
        title: "Your Decision",
        description: "Based on everything you've learned.",
        fields: [
          { id: "go_no_go", label: "Is this a GO, a PIVOT, or do you need to STOP?", type: "radio", options: ["GO — the market wants this", "PIVOT — the idea needs adjustment", "STOP — back to the drawing board"] },
        ]
      },
    ]
  },

  stage2: {
    title: "Business Setup",
    subtitle: "Your idea is validated. Let's make it official.",
    sections: [
      {
        id: "identity",
        title: "Your Business",
        description: "Name it and structure it.",
        fields: [
          { id: "business_name", label: "What's the name of your business?", type: "text", placeholder: "e.g., Kiwi Clean Ltd", required: true },
          { id: "company_structure", label: "What company structure are you going with?", type: "radio", options: ["Sole Trader", "Limited Company (Ltd)", "Partnership", "Not sure yet"] },
        ]
      },
      {
        id: "registrations",
        title: "Registrations",
        description: "The legal foundations.",
        fields: [
          { id: "nzbn_registered", label: "Have you registered your NZBN?", type: "radio", options: ["Yes — all done", "In progress", "Not yet — need to do this"] },
          { id: "gst_registered", label: "GST registration?", helpText: "Compulsory if turnover exceeds $60,000", type: "radio", options: ["Yes — registered", "Not required (under $60k threshold)", "Not yet — need to register"] },
        ]
      },
      {
        id: "finances",
        title: "Finances",
        description: "Track every dollar from day one.",
        fields: [
          { id: "business_bank", label: "Business bank account", type: "radio", options: ["ANZ", "BNZ", "ASB", "Westpac", "Kiwibank", "Other bank", "Haven't set one up yet"] },
          { id: "accounting_system", label: "Accounting system", type: "radio", options: ["Xero", "MYOB", "Hnry", "Other system", "Haven't decided yet"] },
        ]
      },
      {
        id: "operations",
        title: "Operations",
        description: "The final pieces.",
        fields: [
          { id: "insurance_status", label: "Business insurance status", type: "radio", options: ["Public Liability sorted", "Multiple policies in place", "Getting quotes now", "Haven't started on insurance"] },
          { id: "domain_secured", label: "Domain name and online presence", type: "radio", options: ["Yes — domain and socials locked in", "Domain only", "Not yet"] },
        ]
      },
    ]
  },

  stage3: {
    title: "Tech Foundation & MVP",
    subtitle: "Define and build the simplest version of your product that people will pay for.",
    sections: [
      {
        id: "mvp_definition",
        title: "Your MVP",
        description: "What's the minimum viable product?",
        fields: [
          { id: "mvp_description", label: "Describe your MVP in one paragraph", type: "textarea", placeholder: "What's the simplest version of your product/service that solves the core problem?", rows: 4, required: true },
          { id: "core_features", label: "List the 3 must-have features", type: "textarea", placeholder: "1. ...\n2. ...\n3. ...", rows: 3 },
          { id: "nice_to_haves", label: "What's NOT in the MVP? (nice-to-haves for later)", type: "textarea", placeholder: "Features you'll add after launch...", rows: 3 },
        ]
      },
      {
        id: "tech_stack",
        title: "Tools & Technology",
        description: "How will you build it?",
        fields: [
          { id: "build_approach", label: "How are you building your MVP?", type: "radio", options: ["Building it myself", "Hiring a developer/agency", "Using no-code tools (Shopify, Squarespace, etc.)", "Using a mix of approaches", "Not sure yet"] },
          { id: "tools_planned", label: "What tools or platforms will you use?", type: "textarea", placeholder: "e.g., Shopify for the store, Canva for branding, Xero for accounting...", rows: 3 },
        ]
      },
      {
        id: "timeline_budget",
        title: "Plan & Budget",
        description: "Set realistic expectations.",
        fields: [
          { id: "mvp_budget", label: "What's your budget for the MVP build?", type: "radio", options: ["Under $500", "$500 - $2,000", "$2,000 - $5,000", "$5,000 - $10,000", "Over $10,000"] },
          { id: "launch_timeline", label: "When do you want to launch?", type: "radio", options: ["Within 2 weeks", "Within 1 month", "Within 3 months", "No firm deadline yet"] },
          { id: "milestones", label: "Key milestones to get there", type: "textarea", placeholder: "Week 1: ...\nWeek 2: ...\nWeek 3: ...", rows: 4 },
        ]
      },
    ]
  },

  stage4: {
    title: "Capital & Fundraising",
    subtitle: "Figure out how much you need and where to get it.",
    sections: [
      {
        id: "financial_needs",
        title: "Funding Requirements",
        description: "How much do you need to get going?",
        fields: [
          { id: "startup_costs", label: "Estimated startup costs for the first 6 months", type: "radio", options: ["Under $5,000", "$5,000 - $15,000", "$15,000 - $50,000", "$50,000 - $100,000", "Over $100,000"] },
          { id: "monthly_burn", label: "Expected monthly operating costs once launched", type: "text", placeholder: "e.g., $3,000/month" },
          { id: "revenue_timeline", label: "When do you expect first revenue?", type: "radio", options: ["Already earning", "Within 1 month of launch", "Within 3 months", "Within 6 months", "Longer than 6 months"] },
        ]
      },
      {
        id: "funding_sources",
        title: "Funding Sources",
        description: "Where will the money come from?",
        fields: [
          { id: "personal_savings", label: "How much personal capital can you invest?", type: "radio", options: ["None — I need external funding", "Under $5,000", "$5,000 - $20,000", "Over $20,000"] },
          { id: "funding_strategy", label: "What other funding sources are you considering?", type: "textarea", placeholder: "e.g., Business loan, angel investor, Callaghan Innovation grant, friends & family...", rows: 3 },
          { id: "nz_grants", label: "Have you looked into NZ business grants?", type: "radio", options: ["Yes — I've applied", "Yes — researching options", "No — need to look into this", "Not applicable"] },
        ]
      },
      {
        id: "financial_plan",
        title: "Financial Plan",
        description: "The numbers that matter.",
        fields: [
          { id: "pricing_strategy", label: "Your pricing model and expected price point", type: "textarea", placeholder: "How will you charge? How much?", rows: 3 },
          { id: "breakeven_estimate", label: "Estimated time to break even", type: "radio", options: ["Already breaking even", "Within 3 months", "Within 6 months", "Within 12 months", "Longer", "Not sure"] },
        ]
      },
    ]
  },

  stage5: {
    title: "Mentorship & Advisory",
    subtitle: "Build the support network that will accelerate your success.",
    sections: [
      {
        id: "current_network",
        title: "Your Current Network",
        description: "Who do you have in your corner?",
        fields: [
          { id: "existing_mentors", label: "Do you have any mentors or advisors currently?", type: "radio", options: ["Yes — formal mentors", "Yes — informal advice from people I know", "No — I need to find mentors"] },
          { id: "industry_connections", label: "How connected are you in your industry?", type: "radio", options: ["Very connected — strong network", "Somewhat — a few useful contacts", "Not very — need to build my network", "Starting fresh in a new industry"] },
          { id: "support_people", label: "Name 2-3 people who could help your business (and how)", type: "textarea", placeholder: "e.g., Sarah — accountant friend who can help with finances...", rows: 3 },
        ]
      },
      {
        id: "advisory_needs",
        title: "What You Need",
        description: "Where are the gaps?",
        fields: [
          { id: "expertise_gaps", label: "What expertise do you need that you don't currently have?", type: "textarea", placeholder: "e.g., Legal, marketing, tech, sales...", rows: 3 },
          { id: "advisory_board", label: "Are you open to forming a small advisory board?", type: "radio", options: ["Yes — great idea", "Maybe — need to learn more", "No — too early for that"] },
          { id: "networking_plan", label: "How will you meet the right people?", type: "textarea", placeholder: "e.g., Join local business groups, attend startup meetups, reach out on LinkedIn...", rows: 3 },
        ]
      },
    ]
  },

  stage6: {
    title: "Growth & Brand",
    subtitle: "Time to get customers. Build your brand and go to market.",
    sections: [
      {
        id: "brand_identity",
        title: "Your Brand",
        description: "How will people know you?",
        fields: [
          { id: "brand_positioning", label: "In one sentence — what makes you different from competitors?", type: "text", placeholder: "e.g., The only eco-friendly cleaning service in Auckland that uses zero chemicals", required: true },
          { id: "brand_voice", label: "How does your brand sound?", type: "radio", options: ["Professional and trustworthy", "Friendly and approachable", "Bold and disruptive", "Warm and caring", "Technical and expert"] },
          { id: "brand_assets", label: "What brand assets do you have?", type: "textarea", placeholder: "e.g., Logo, website, social media profiles, business cards...", rows: 3 },
        ]
      },
      {
        id: "marketing",
        title: "Go to Market",
        description: "How will you reach your customers?",
        fields: [
          { id: "marketing_channels", label: "Which marketing channels will you focus on?", type: "textarea", placeholder: "e.g., Instagram, Google Ads, local networking, word of mouth, trade shows...", rows: 3 },
          { id: "marketing_budget", label: "Monthly marketing budget", type: "radio", options: ["$0 — organic only", "Under $500/month", "$500 - $2,000/month", "Over $2,000/month"] },
          { id: "first_customers_plan", label: "How will you get your first 10 paying customers?", type: "textarea", placeholder: "Be specific about your plan...", rows: 4 },
        ]
      },
      {
        id: "launch",
        title: "Launch Plan",
        description: "Make it happen.",
        fields: [
          { id: "launch_readiness", label: "How ready are you to launch?", type: "radio", options: ["Already launched!", "Ready — launching this week", "Almost — a few things to finish", "Weeks away — still building", "Not ready yet"] },
          { id: "launch_plan", label: "Describe your launch plan", type: "textarea", placeholder: "How will you announce? Any launch promotions? Who will you tell first?", rows: 4 },
        ]
      },
    ]
  },

  stage7: {
    title: "Scaling Operations",
    subtitle: "You've built it. Now scale it — systems, team, and growth.",
    sections: [
      {
        id: "current_state",
        title: "Where You're At",
        description: "Take stock of your business today.",
        fields: [
          { id: "monthly_revenue", label: "Current monthly revenue", type: "radio", options: ["Pre-revenue", "Under $1,000/month", "$1,000 - $5,000/month", "$5,000 - $20,000/month", "Over $20,000/month"] },
          { id: "customer_count", label: "How many active customers/clients do you have?", type: "text", placeholder: "e.g., 15 recurring clients" },
          { id: "biggest_bottleneck", label: "What's the biggest bottleneck to growth right now?", type: "textarea", placeholder: "What's holding you back?", rows: 3 },
        ]
      },
      {
        id: "team",
        title: "Team & Operations",
        description: "You can't scale alone.",
        fields: [
          { id: "team_status", label: "Current team", type: "radio", options: ["Just me", "Me + 1 person", "Small team (2-5)", "Growing team (5+)"] },
          { id: "next_hire", label: "What's the next role you need to fill?", type: "text", placeholder: "e.g., Part-time marketing person, virtual assistant..." },
          { id: "systems_in_place", label: "What systems and processes do you have?", type: "textarea", placeholder: "e.g., CRM for customer management, automated invoicing, SOPs for delivery...", rows: 3 },
        ]
      },
      {
        id: "scale_plan",
        title: "Growth Plan",
        description: "Where to from here?",
        fields: [
          { id: "twelve_month_goal", label: "Where do you want to be in 12 months?", type: "textarea", placeholder: "Revenue target, customer count, team size...", rows: 3, required: true },
          { id: "scale_strategy", label: "What's your strategy to get there?", type: "textarea", placeholder: "How will you double or triple your revenue?", rows: 4 },
          { id: "support_needed", label: "What support do you still need?", type: "textarea", placeholder: "From SPRING, mentors, the community...", rows: 3 },
        ]
      },
    ]
  },
};

// ============================================================================
// MAIN FORM COMPONENT
// ============================================================================

const StageForm = () => {
  const { stageNum } = useParams();
  const stageId = stageNum === '00' ? 'stage00' : `stage${stageNum}`;
  const navigate = useNavigate();
  const { token, user } = useAuth();

  // Map route num to display number (00→1, 0→2, 1→3, ... 7→9)
  const DISPLAY_NUM = { '00': '1', '0': '2', '1': '3', '2': '4', '3': '5', '4': '6', '5': '7', '6': '8', '7': '9' };
  const displayNum = DISPLAY_NUM[stageNum] || stageNum;

  // Payment gate: stage00 is free, stage0+ requires payment
  const isPaidStage = stageId !== 'stage00';
  const isLocked = isPaidStage && !user?.stages_unlocked;

  // Pathway scope gate: block stages outside user's assigned pathway
  const pathwayStages = user?.pathway_stages || [];
  const isOutsidePathway = isPaidStage && pathwayStages.length > 0 && !pathwayStages.includes(stageId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [reportHtml, setReportHtml] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [templates, setTemplates] = useState(null);
  const celebrationRef = useRef(null);

  // Resolve form config: stage0 uses pathway-specific form, others use generic
  const userPathway = user?.pathway;
  const formConfig = stageId === 'stage0' && userPathway && STAGE2_FORMS[userPathway]
    ? STAGE2_FORMS[userPathway]
    : STAGE_FORMS[stageId];

  useEffect(() => {
    if (isOutsidePathway) { setLoading(false); return; }
    if (formConfig && !isLocked) loadStatus();
    else if (isLocked) setLoading(false);
  }, [stageId]);

  // Fetch templates when Stage 2 (stage0) is in review mode
  useEffect(() => {
    if (stageId === 'stage0' && reviewMode && user?.pathway && user?.stages_unlocked) {
      axios.get(`${API}/templates/${user.pathway}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setTemplates(res.data)).catch(() => {});
    }
  }, [stageId, reviewMode, user?.pathway]);

  const loadStatus = async () => {
    try {
      const endpoint = stageId === 'stage00'
        ? `${API}/stage00/status`
        : `${API}/stage-assessment/${stageId}/status`;

      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to current stage if this stage is locked
      if (!res.data.assessment_complete && res.data.current_stage !== stageId) {
        const STAGE_ORDER = ['stage00','stage0','stage1','stage2','stage3','stage4','stage5','stage6','stage7'];
        const thisIdx = STAGE_ORDER.indexOf(stageId);
        const currentIdx = STAGE_ORDER.indexOf(res.data.current_stage);
        if (thisIdx > currentIdx) {
          toast.error('Complete earlier stages first');
          navigate('/portal/stages');
          return;
        }
      }

      if (res.data.assessment_complete) {
        setReviewMode(true);
        setFormData(res.data.assessment_data || {});
        try {
          const reportRes = await axios.get(`${API}/stage-assessment/${stageId}/report`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setReportHtml(reportRes.data.report_html);
          setShowReport(true);
        } catch {}
      } else if (res.data.assessment_data && Object.keys(res.data.assessment_data).length > 0) {
        setFormData(res.data.assessment_data);
        // Jump to the first incomplete section
        const sections = formConfig.sections;
        for (let i = 0; i < sections.length; i++) {
          const hasAll = sections[i].fields.every(f => res.data.assessment_data[f.id]);
          if (!hasAll) { setCurrentSection(i); break; }
          if (i === sections.length - 1) setCurrentSection(i);
        }
      }
    } catch (error) {
      console.error('Error loading status:', error);
    }
    setLoading(false);
  };

  const updateField = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const saveProgress = useCallback(async (data) => {
    setSaving(true);
    try {
      const endpoint = stageId === 'stage00'
        ? `${API}/stage00/save-progress`
        : `${API}/stage-assessment/${stageId}/save-progress`;

      await axios.post(endpoint, data || formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
    setSaving(false);
  }, [stageId, formData, token]);

  const goNext = async () => {
    await saveProgress(formData);
    if (currentSection < formConfig.sections.length - 1) {
      setCurrentSection(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goBack = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const fireConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ['#F6ADBC', '#1a1a1a', '#ffffff', '#F6ADBC', '#F6ADBC'];

    // Initial big burst
    confetti({ particleCount: 100, spread: 80, origin: { y: 0.6 }, colors });

    // Continuous sparkle
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  const completeStage = async () => {
    setCompleting(true);
    try {
      const endpoint = stageId === 'stage00'
        ? `${API}/stage00/complete`
        : `${API}/stage-assessment/${stageId}/complete`;

      const res = await axios.post(endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReportHtml(res.data.report_html);
      setReviewMode(true);

      // Show celebration first
      setShowCelebration(true);
      fireConfetti();
    } catch (error) {
      toast.error(formatErrorDetail(error.response?.data?.detail) || 'Could not complete stage');
    }
    setCompleting(false);
  };

  const dismissCelebration = () => {
    setShowCelebration(false);
    setShowReport(true);
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/stage-report/${stageId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SPRING_Stage${stageNum}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch {
      toast.error('Could not download PDF');
    }
  };

  if (!formConfig) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-black/40">Stage {stageNum} form coming soon</p>
      </div>
    );
  }

  if (isOutsidePathway) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="stage-outside-pathway">
        <div className="max-w-md text-center">
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <Lock className="h-8 w-8" style={{ color: '#999' }} />
          </div>
          <h1 className="text-2xl font-bold text-black mb-3" style={{ fontFamily: 'Arial, sans-serif' }}>
            Stage {displayNum} is not in your pathway
          </h1>
          <p className="text-black/60 mb-8 text-sm leading-relaxed">
            This stage is outside the scope of your current pathway. Return to your journey to continue with your assigned stages.
          </p>
          <button
            onClick={() => navigate('/portal/stages')}
            data-testid="outside-pathway-back-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#1a1a1a', color: '#fff', border: 'none',
              padding: '14px 28px', borderRadius: 0,
              fontSize: '15px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Back to My Journey
          </button>
        </div>
      </div>
    );
  }

  if (isLocked) {
    const handleCheckout = async () => {
      setCheckoutLoading(true);
      try {
        const pathwayPackageId = user?.pathway === 'A' ? 'pathway_a' : user?.pathway === 'B' ? 'pathway_b' : user?.pathway === 'C' ? 'pathway_c' : 'pathway_a';
        const origin = window.location.origin;
        const res = await axios.post(`${API}/payments/create-checkout`, {
          package_id: pathwayPackageId,
          origin_url: origin,
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.url) {
          window.location.href = res.data.url;
        }
      } catch (e) {
        toast.error(formatErrorDetail(e.response?.data?.detail) || 'Failed to start checkout');
        setCheckoutLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="stage-locked-gate">
        <div className="max-w-md text-center">
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <Lock className="h-8 w-8" style={{ color: '#999' }} />
          </div>
          <h1 className="text-2xl font-bold text-black mb-3" style={{ fontFamily: 'Arial, sans-serif' }}>
            Stage {displayNum} is locked
          </h1>
          <p className="text-black/60 mb-2 text-sm leading-relaxed">
            Unlock your pathway to access this stage. Pricing is $TBA.
          </p>
          <p className="text-black/40 text-xs mb-8">
            {formConfig.title} — {formConfig.subtitle}
          </p>

          <button
            onClick={handleCheckout}
            disabled={checkoutLoading}
            data-testid="stage-locked-unlock-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: '#F6ADBC', color: '#1E491A', border: 'none',
              padding: '16px 36px', borderRadius: 0,
              fontSize: '16px', fontWeight: 700, cursor: checkoutLoading ? 'wait' : 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              marginBottom: '16px',
            }}
            onMouseEnter={(e) => { if (!checkoutLoading) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; } }}
            onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
          >
            {checkoutLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>Unlock Pathway — $TBA</>
            )}
          </button>

          <div>
            <button
              onClick={async () => {
                try {
                  const pathwayPkg = user?.pathway === 'A' ? 'pathway_a' : user?.pathway === 'B' ? 'pathway_b' : user?.pathway === 'C' ? 'pathway_c' : 'pathway_a';
                  const ax = (await import('axios')).default;
                  await ax.post(`${API}/payments/demo-bypass`, { package_id: pathwayPkg }, { headers: { Authorization: `Bearer ${token}` } });
                  window.location.reload();
                } catch {}
              }}
              data-testid="stage-locked-demo-bypass-btn"
              style={{
                background: 'none', border: '1px solid #ddd', color: '#999',
                padding: '10px 20px', borderRadius: 0,
                fontSize: '13px', cursor: 'pointer', marginBottom: '12px',
              }}
            >
              Skip Payment (Demo)
            </button>
          </div>

          <div>
            <button
              onClick={() => navigate('/portal/stages')}
              data-testid="stage-locked-back-btn"
              style={{
                background: 'none', border: 'none', color: '#999',
                fontSize: '14px', cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              Back to My Journey
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-black/40" />
      </div>
    );
  }

  const section = formConfig.sections[currentSection];
  const totalSections = formConfig.sections.length;
  const progress = reviewMode ? 100 : Math.round(((currentSection + 1) / totalSections) * 100);
  const isLastSection = currentSection === totalSections - 1;

  // Check if current section is filled enough to proceed
  const requiredFields = section.fields.filter(f => f.required);
  const canProceed = requiredFields.every(f => formData[f.id]?.trim());

  return (
    <div className="min-h-screen bg-white" data-testid={`stage-${stageNum}-form`}>
      {/* Celebration Overlay */}
      {showCelebration && (
        <div
          ref={celebrationRef}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1E491A]/95 backdrop-blur-sm"
          data-testid="celebration-overlay"
        >
          <div className="max-w-lg mx-auto px-6 text-center animate-in fade-in zoom-in duration-500">
            {/* Gold sparkle icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-none bg-[#F6ADBC] flex items-center justify-center  shadow-[#F6ADBC]/30">
              <Sparkles className="h-10 w-10 text-[#1E491A]" />
            </div>

            {/* Stage badge */}
            <p className="text-[#F6ADBC] text-sm font-bold uppercase tracking-widest mb-3">
              Stage {stageNum}
            </p>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
              {(stageId === 'stage0' && userPathway && STAGE2_CELEBRATIONS[userPathway]?.headline) || BRIAN_CELEBRATIONS[stageId]?.headline || 'Stage Complete!'}
            </h1>

            {/* Brian's message */}
            <p className="text-lg text-white/80 leading-relaxed mb-6" style={{ fontFamily: 'Arial, sans-serif' }}>
              {(stageId === 'stage0' && userPathway && STAGE2_CELEBRATIONS[userPathway]?.message) || BRIAN_CELEBRATIONS[stageId]?.message || 'Great work. You\'re making real progress.'}
            </p>

            {/* Next stage hint */}
            <p className="text-sm text-[#F6ADBC]/70 mb-8">
              {(stageId === 'stage0' && userPathway && STAGE2_CELEBRATIONS[userPathway]?.next) || BRIAN_CELEBRATIONS[stageId]?.next || ''}
            </p>

            {/* Signature */}
            <p className="text-white/50 text-sm mb-8 italic">— Brian</p>

            {/* CTA */}
            <Button
              onClick={dismissCelebration}
              className="h-14 px-10 bg-[#F6ADBC] text-[#1E491A] hover:bg-[#e6c200] rounded-none font-bold text-lg  shadow-[#F6ADBC]/20"
              data-testid="celebration-continue-btn"
            >
              View Your Report
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-2 bg-[#E5E5E5]">
        <div
          className="h-full bg-[#F6ADBC] transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-2 z-40 bg-white/90 backdrop-blur-sm border-b border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/portal/stages')}
            className="flex items-center gap-2 text-[#666666] hover:text-[#1E491A] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">My Journey</span>
          </button>
          <div className="text-center">
            <p className="text-xs text-[#666666] uppercase tracking-widest font-semibold">
              {formConfig.title || `Stage ${stageNum}`}{reviewMode ? ' — Review' : ''}
            </p>
          </div>
          <div className="text-xs text-[#666666]">
            {saving ? 'Saving...' : reviewMode ? 'Complete' : `${currentSection + 1} / ${totalSections}`}
          </div>
        </div>
      </header>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Section Title */}
        <div className="mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1E491A] mb-2">
            {reviewMode ? formConfig.title : section.title}
          </h1>
          <p className="text-lg text-[#666666] leading-relaxed">
            {reviewMode ? 'Review your responses below.' : section.description}
          </p>
        </div>

        {/* Fields */}
        {reviewMode ? (
          // REVIEW MODE: Show all sections and answers
          <div className="space-y-10">
            {formConfig.sections.map((sec) => (
              <div key={sec.id} className="space-y-6">
                <h2 className="text-xl font-semibold text-[#1E491A] pb-2 border-b border-[#E5E5E5]">{sec.title}</h2>
                {sec.fields.map((field) => (
                  <div key={field.id}>
                    <label className="block text-sm text-[#666666] mb-1">{field.label}</label>
                    <p className="text-[#1E491A] text-lg" data-testid={`review-${field.id}`}>
                      {formData[field.id] || <span className="text-[#999]">Not answered</span>}
                    </p>
                  </div>
                ))}
              </div>
            ))}

            {/* Stage 2 Template Downloads */}
            {stageId === 'stage0' && templates && (
              <div className="mt-10 pt-8 border-t-2 border-[#F6ADBC]/30" data-testid="stage2-templates">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-none bg-[#F6ADBC]/10 flex items-center justify-center">
                    <FolderDown className="h-5 w-5 text-[#F6ADBC]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#1E491A]">{templates.label}</h2>
                    <p className="text-sm text-[#666]">Download your documents below</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {templates.files.map((file, idx) => (
                    <a
                      key={idx}
                      href={file.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`template-download-${idx}`}
                      className="flex items-center gap-3 p-4 rounded-none border border-[#E5E5E5] bg-[#FAFAFA] hover:bg-[#F6ADBC]/5 hover:border-[#F6ADBC]/40 transition-all group"
                    >
                      <div className="w-8 h-8 rounded-none bg-white border border-[#E5E5E5] flex items-center justify-center flex-shrink-0 group-hover:border-[#F6ADBC]/40">
                        <FileText className="h-4 w-4 text-[#999] group-hover:text-[#F6ADBC]" />
                      </div>
                      <span className="flex-1 text-sm font-medium text-[#1E491A]">{file.name}</span>
                      <span className="text-xs text-[#999] uppercase tracking-wide">
                        {file.url.endsWith('.xlsx') ? 'xlsx' : 'docx'}
                      </span>
                      <Download className="h-4 w-4 text-[#ccc] group-hover:text-[#F6ADBC] transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // ACTIVE MODE: Show current section fields
          <div className="space-y-8">
            {section.fields.map((field) => (
              <FormField
                key={field.id}
                field={field}
                value={formData[field.id] || ''}
                onChange={(val) => updateField(field.id, val)}
                disabled={reviewMode}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer / Navigation */}
      <footer className="sticky bottom-0 bg-white border-t border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto px-6 py-4">
          {showReport ? (
            <div className="space-y-3">
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    const win = window.open('', '_blank');
                    win.document.write(reportHtml);
                    win.document.close();
                  }}
                  variant="outline"
                  className="flex-1 h-12 rounded-none border-[#E5E5E5] text-[#1E491A]"
                  data-testid="view-report-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Report
                </Button>
                <Button
                  onClick={downloadPdf}
                  variant="outline"
                  className="flex-1 h-12 rounded-none border-[#E5E5E5] text-[#1E491A]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
              <Button
                onClick={() => navigate('/portal/stages')}
                className="w-full h-12 bg-[#1E491A] text-white hover:bg-black rounded-none font-bold"
                data-testid="back-journey-btn"
              >
                Back to My Journey
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {currentSection > 0 && (
                <Button
                  onClick={goBack}
                  variant="outline"
                  className="h-12 px-6 rounded-none border-[#E5E5E5]"
                  data-testid="back-section-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
              <div className="flex-1" />
              {isLastSection ? (
                <Button
                  onClick={completeStage}
                  disabled={completing}
                  className="h-12 px-8 bg-[#F6ADBC] text-[#1E491A] hover:bg-[#e6c200] rounded-none font-bold text-lg"
                  data-testid="complete-btn"
                >
                  {completing ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Check className="h-5 w-5 mr-2" />
                  )}
                  Complete Stage {stageNum}
                </Button>
              ) : (
                <Button
                  onClick={goNext}
                  className="h-12 px-8 bg-[#F6ADBC] text-[#1E491A] hover:bg-[#e6c200] rounded-none font-bold text-lg"
                  data-testid="next-btn"
                >
                  Continue
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

// ============================================================================
// INDIVIDUAL FORM FIELD COMPONENT
// ============================================================================

const FormField = ({ field, value, onChange, disabled }) => {
  if (field.type === 'text') {
    return (
      <div>
        <label className="block text-lg md:text-xl leading-relaxed text-[#1E491A] mb-3 font-medium">
          {field.label}
          {field.required && <span className="text-[#FF3B30] ml-1">*</span>}
        </label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          className="h-14 text-lg bg-[#F9F9F9] border-2 border-[#E5E5E5] focus:border-[#F6ADBC] focus:ring-0 rounded-none px-4"
          data-testid={`field-${field.id}`}
        />
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div>
        <label className="block text-lg md:text-xl leading-relaxed text-[#1E491A] mb-3 font-medium">
          {field.label}
          {field.required && <span className="text-[#FF3B30] ml-1">*</span>}
        </label>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          rows={field.rows || 4}
          className="text-lg bg-[#F9F9F9] border-2 border-[#E5E5E5] focus:border-[#F6ADBC] focus:ring-0 rounded-none px-4 py-3 resize-none"
          data-testid={`field-${field.id}`}
        />
      </div>
    );
  }

  if (field.type === 'radio') {
    return (
      <div>
        <label className="block text-lg md:text-xl leading-relaxed text-[#1E491A] mb-3 font-medium">
          {field.label}
          {field.required && <span className="text-[#FF3B30] ml-1">*</span>}
        </label>
        {field.helpText && (
          <p className="text-sm text-[#666666] mb-3">{field.helpText}</p>
        )}
        <div className="space-y-2">
          {field.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => !disabled && onChange(option)}
              disabled={disabled}
              className={`w-full text-left p-4 rounded-none border-2 transition-all duration-200 ${
                value === option
                  ? 'border-[#F6ADBC] bg-[#F6ADBC]/10'
                  : 'border-[#E5E5E5] bg-[#F9F9F9] hover:border-[#F6ADBC]/50'
              }`}
              data-testid={`option-${field.id}-${idx}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-none border-2 flex items-center justify-center flex-shrink-0 ${
                  value === option ? 'border-[#F6ADBC] bg-[#F6ADBC]' : 'border-[#999]'
                }`}>
                  {value === option && <div className="w-2 h-2 rounded-none bg-[#1E491A]" />}
                </div>
                <span className="text-[#1E491A]">{option}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default StageForm;
